import { ref, watch, computed } from "@vue/composition-api";
import store from "@/store";
import { title } from "@core/utils/filter";

// Notification
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import router from "@/router";

export default function useUsersList() {
  // Use toast
  const toast = useToast();

  const refUserListTable = ref(null);

  // Table Handlers
  const tableColumns = [
    { key: "id", label: "#Order", sortable: true },
    { key: "created", label: "date", sortable: true },
    { key: "customer", sortable: true },
    { key: "total_order", label: "Total", sortable: true },
    { key: "total_tips", label: "tips", sortable: true },
  ];
  const perPage = ref(10);
  const totalUsers = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref("");
  const sortBy = ref("id");
  const isSortDirDesc = ref(true);
  const roleFilter = ref(null);
  const statusFilter = ref(null);
  const itemsCustomers = ref([]);
  const itemsStatus = ref([]);
  const rangeDateFilter = ref(null);

  const dataMeta = computed(() => {
    const localItemsCount = refUserListTable.value
      ? refUserListTable.value.localItems.length
      : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalUsers.value,
    };
  });

  const refetchData = () => {
    refUserListTable.value.refresh();
  };

  watch(
    [
      currentPage,
      perPage,
      searchQuery,
      roleFilter,
      statusFilter,
      rangeDateFilter,
    ],
    () => {
      refetchData();
    }
  );

  const fetchUsers = (ctx, callback) => {
    store
      .dispatch("app-user/fetchOfficeCustomers", {
        q: searchQuery.value.toLowerCase(),
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        customerId: roleFilter.value,
        status: statusFilter.value,
        officeId: router.currentRoute.params.id,
        rangeDate: rangeDateFilter.value,
      })
      .then((response) => {
        const { invoices, sales, total1 } = response.data;
        callback(sales);
        totalUsers.value = total1;
        const arrayCustomers = [];
        if (total1 > 0) {
          invoices.map((data, item) => {
            arrayCustomers.push({
              label: `${data.customer.first_name} ${data.customer.last_name}`,
              value: data.customerId,
            });
          });

          itemsCustomers.value = arrayCustomers;
        }
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error fetching users list",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserRoleVariant = (role) => {
    if (role === "subscriber") return "primary";
    if (role === "author") return "warning";
    if (role === "maintainer") return "success";
    if (role === "editor") return "info";
    if (role === "admin") return "danger";
    return "primary";
  };

  const resolveUserRoleIcon = (role) => {
    if (role === "subscriber") return "UserIcon";
    if (role === "author") return "SettingsIcon";
    if (role === "maintainer") return "DatabaseIcon";
    if (role === "editor") return "Edit2Icon";
    if (role === "admin") return "ServerIcon";
    return "UserIcon";
  };

  const resolveUserStatusVariant = (status) => {
    if (status === true) return "success";
    if (status === false) return "warning";
    return "success";
  };

  const dataItemCustomers = itemsCustomers;
  const dataItemStatus = itemsStatus;

  return {
    fetchUsers,
    tableColumns,
    perPage,
    currentPage,
    totalUsers,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refUserListTable,

    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveUserStatusVariant,
    refetchData,

    // Extra Filters
    roleFilter,
    statusFilter,
    dataItemStatus,
    dataItemCustomers,
    rangeDateFilter,
  };
}
